import { zodResolver } from "@hookform/resolvers/zod";
import { Send01 } from "@untitledui/icons";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod/v3";
import { useAddCreatorToTargetCollaboration, useBulkAddCreatorsToTargetCollaboration } from "@/api/collaborations";
import { BulkInviteResultsDialog } from "@/components/assurance/bulk-invite-results-dialog";
import { Button } from "@/components/base/buttons/button";
import { Dialog, DialogContent, DialogFooter } from "@/components/ui/dialog";
import { useBulkActionContext } from "@/providers/bulk-action-progress-provider";
import { error, success } from "@/providers/ToastProvider";

type Creator = {
  openId: string;
  username: string;
  nickname?: string | null;
  avatar?: string | null;
};

type QuickInviteModalProps = {
  creator?: Creator;
  creators?: Creator[];
  collaborationId: string;
  collaborationName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // Receives the freshly published collab id when the target was a draft, so the
  // caller can record analytics against the real id rather than the now-stale draft id.
  onSuccess?: (publishedCollaborationId?: string) => void;
  /**
   * Set when the invite originates from a recommendation surface. Forwarded to the API so the
   * server records the `send_creator_invite` NBA interaction for each invited creator —
   * replaces the per-creator POST the FE used to fire to /recommendations/invite.
   */
  nbaModelVersion?: string;
};

const DONT_SHOW_AGAIN_KEY = "quick-invite-modal-dont-show";

const schema = z.object({
  sendDM: z.boolean(),
  dontShowAgain: z.boolean(),
});

type FormValues = z.infer<typeof schema>;

export const QuickInviteModal = ({
  creator,
  creators,
  collaborationId,
  collaborationName,
  open,
  onOpenChange,
  onSuccess,
  nbaModelVersion,
}: QuickInviteModalProps) => {
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { sendDM: false, dontShowAgain: false },
  });
  const { mutateAsync: inviteCreator, isPending: isSinglePending } = useAddCreatorToTargetCollaboration();
  const { mutateAsync: bulkInviteCreators, isPending: isBulkPending } = useBulkAddCreatorsToTargetCollaboration();
  const { startTracking: startBulkTracking } = useBulkActionContext();

  const [resultsDialog, setResultsDialog] = useState<{
    results: Array<{ creatorUsername: string; success: boolean; reason?: string }>;
    wasDraftPublished: boolean;
  } | null>(null);

  const isPending = isSinglePending || isBulkPending;

  const creatorsList = useMemo(() => {
    if (creators && creators.length > 0) return creators;
    if (creator) return [creator];
    return [];
  }, [creator, creators]);

  const isSingleCreator = creatorsList.length === 1;
  const displayName = isSingleCreator ? creatorsList[0]?.nickname || creatorsList[0]?.username || "" : null;

  const handleSubmit = form.handleSubmit(async (values) => {
    try {
      let publishedCollaborationId: string | undefined;
      let showResultsDialogFor: {
        results: Array<{ creatorUsername: string; success: boolean; reason?: string }>;
        wasDraftPublished: boolean;
      } | null = null;

      if (isSingleCreator) {
        const result = await inviteCreator({
          targetCollaborationId: collaborationId,
          creatorOpenId: creatorsList[0].openId,
          sendDM: values.sendDM,
          ...(nbaModelVersion && { nbaModelVersion }),
        });
        if ("publishedCollaborationId" in result) {
          publishedCollaborationId = result.publishedCollaborationId;
        }
        success(
          publishedCollaborationId
            ? `Draft collaboration published to TikTok and ${displayName} was invited`
            : `Invited ${displayName} to collaboration`,
        );
      } else {
        const result = await bulkInviteCreators({
          targetCollaborationId: collaborationId,
          creatorOpenIds: creatorsList.map((c) => c.openId),
          sendDM: values.sendDM,
          ...(nbaModelVersion && { nbaModelVersion }),
        });

        if ("async" in result) {
          startBulkTracking(result.jobId, "invite_to_collaboration", null);
        } else {
          const { summary, results } = result;
          publishedCollaborationId = result.publishedCollaborationId;
          const wasDraftPublished = !!publishedCollaborationId;

          if (summary.succeeded === summary.total) {
            success(
              wasDraftPublished
                ? `Draft collaboration published to TikTok and ${summary.succeeded} creator${summary.succeeded > 1 ? "s" : ""} invited`
                : `Successfully invited ${summary.succeeded} creator${summary.succeeded > 1 ? "s" : ""} to collaboration`,
            );
          } else {
            // Partial failure (all-fail throws server-side and lands in catch). Map the API's
            // open-id-keyed results back to usernames using the input list so the dialog can
            // render @{handle} rows.
            const usernameByOpenId = new Map(creatorsList.map((c) => [c.openId, c.username]));
            const enrichedResults = results.map((r) => ({
              creatorUsername: usernameByOpenId.get(r.creatorOpenId) ?? r.creatorOpenId,
              success: r.success,
              reason: r.reason,
            }));
            showResultsDialogFor = { results: enrichedResults, wasDraftPublished };
          }
        }
      }

      if (values.dontShowAgain) {
        localStorage.setItem(DONT_SHOW_AGAIN_KEY, "true");
      }

      onSuccess?.(publishedCollaborationId);
      onOpenChange(false);
      form.reset();
      if (showResultsDialogFor) setResultsDialog(showResultsDialogFor);
    } catch (err) {
      error(err instanceof Error ? err.message : "Failed to invite creator");
    }
  });

  const sendDM = form.watch("sendDM");
  const dontShowAgain = form.watch("dontShowAgain");

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          onOpenChange(next);
          if (!next) form.reset();
        }}
      >
        <DialogContent className="sm:max-w-lg p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Icon */}
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-gray-100">
              <Send01 className="w-6 h-6 text-gray-600" />
            </div>

            {/* Title and description */}
            <div className="space-y-2">
              <h2 className="text-lg font-semibold text-primary">
                {isSingleCreator && displayName
                  ? `Invite ${displayName} and review the next creator`
                  : `Invite ${creatorsList.length} creators and keep going`}
              </h2>
              <p className="text-sm text-tertiary">
                {isSingleCreator && displayName ? (
                  <>
                    The preset message for <em>"{collaborationName}"</em> will be sent to <em>"{displayName}"</em> on
                    TikTok.
                  </>
                ) : (
                  <>
                    Collaboration invites for <em>"{collaborationName}"</em> will be sent to all {creatorsList.length}{" "}
                    selected creators on TikTok.
                  </>
                )}
              </p>
            </div>

            {/* DM toggle */}
            <button
              type="button"
              className="flex items-center gap-3 w-full rounded-lg px-4 py-4 cursor-pointer text-left"
              style={{ backgroundColor: "#F5F5F5" }}
              onClick={() => form.setValue("sendDM", !sendDM)}
            >
              {/* Custom toggle track */}
              <div
                className="relative flex-shrink-0 w-9 h-5 rounded-full transition-all duration-150"
                style={
                  sendDM
                    ? { backgroundColor: "var(--color-brand-600, #6941C6)" }
                    : { backgroundColor: "#FFFFFF", border: "1px solid #D5D7DA" }
                }
              >
                {/* Toggle thumb */}
                <div
                  className="absolute top-0.5 size-4 rounded-full shadow-sm transition-all duration-150"
                  style={
                    sendDM ? { right: "2px", backgroundColor: "#FFFFFF" } : { left: "2px", backgroundColor: "#A4A7AE" }
                  }
                />
              </div>
              <span className="text-sm text-primary">Include a card invite in their TikTok DMs</span>
            </button>

            <DialogFooter className="flex flex-row items-center justify-between sm:justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={dontShowAgain}
                  onChange={(e) => form.setValue("dontShowAgain", e.target.checked)}
                  className="w-4 h-4 rounded border border-gray-300 cursor-pointer accent-brand-600"
                />
                <span className="text-sm text-tertiary">Don't show again</span>
              </label>

              <div className="flex items-center gap-3">
                <Button color="secondary" size="lg" onClick={() => onOpenChange(false)} isDisabled={isPending}>
                  Cancel
                </Button>
                <Button color="primary" size="lg" type="submit" isDisabled={isPending} isLoading={isPending}>
                  Send Invite
                </Button>
              </div>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <BulkInviteResultsDialog
        open={!!resultsDialog}
        onOpenChange={(o) => {
          if (!o) setResultsDialog(null);
        }}
        results={resultsDialog?.results ?? []}
        wasDraftPublished={resultsDialog?.wasDraftPublished ?? false}
      />
    </>
  );
};

export const shouldShowQuickInviteModal = (): boolean => {
  return localStorage.getItem(DONT_SHOW_AGAIN_KEY) !== "true";
};
